import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { garageAPI } from '../../../services/garage/api';
import '../AIGarage.css';

const RequestInfoModal = ({ isOpen, idea, adminEmail, adminName, onClose, onSuccess }) => {
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await garageAPI.requestMoreInfo(idea.id, {
        adminEmail,
        adminName,
        message: message.trim(),
        priority
      });

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to request info:', err);
      setError('Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>Request More Information</h2>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <div className="garage-request-info-context">
            <p><strong>Request:</strong> {idea.title}</p>
            <p><strong>From:</strong> {idea.requesterName || idea.requesterEmail}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="garage-form-group">
              <label htmlFor="info-message">
                What information do you need from the requestor? *
              </label>
              <textarea
                id="info-message"
                className="garage-textarea"
                rows="4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please provide more details about..."
                required
              />
            </div>

            <div className="garage-form-group">
              <label htmlFor="info-priority">Priority</label>
              <select
                id="info-priority"
                className="garage-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="normal">Normal - Nice to have</option>
                <option value="important">Important - Helps prioritization</option>
                <option value="blocking">Blocking - Cannot proceed without this</option>
              </select>
            </div>

            {error && (
              <div className="garage-error-message">{error}</div>
            )}

            <div className="garage-modal-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="garage-btn garage-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default RequestInfoModal;
